// RoleGate — wraps a role-restricted subtree.
//
// Renders children when the current user's role is in the allow-list,
// otherwise renders a 403 placeholder. Does NOT redirect — the user is
// already authenticated; they just lack permission. This matches the
// brief: "render null or a 403 placeholder (not a redirect)".

import { Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';
import { useAuthStore } from '../store/authStore';
import type { UserSummary } from '../types/api';

interface RoleGateProps {
  roles: UserSummary['role'][];
  children?: ReactNode;
  fallback?: ReactNode;
}

export function RoleGate({ roles, children, fallback }: RoleGateProps) {
  const user = useAuthStore((s) => s.user);

  if (!user || !roles.includes(user.role)) {
    return <>{fallback ?? <ForbiddenPlaceholder />}</>;
  }

  return <>{children ?? <Outlet />}</>;
}

function ForbiddenPlaceholder() {
  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        textAlign: 'center',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
      role="alert"
    >
      <h1 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>403 — Forbidden</h1>
      <p style={{ color: 'var(--color-text-secondary, #475569)' }}>
        Your account does not have access to this area.
      </p>
    </div>
  );
}
